import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '@/hooks/use-toast';

type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

type Device = {
  id: string;
  created_at: string;
  updated_at: string;
  code: string;
  user_id?: string;
  organization_id?: string;
  name: string;
  status: string;
  last_active?: string;
  screen_id?: string;
  settings?: Json;
};

type DeviceUpdate = {
  name?: string;
  status?: string;
  screen_id?: string;
  settings?: Json;
  user_id?: string;
};

// Função para obter o limite de dispositivos do plano do usuário
const getUserPlanDeviceLimit = async (userId: string): Promise<{ limit: number; planName: string }> => {
  try {
    // Buscar status da assinatura do usuário
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('subscription_status')
      .eq('id', userId)
      .single();

    if (profileError) {
      console.error('Erro ao buscar perfil do usuário:', profileError);
      throw profileError;
    }

    // Se tem assinatura ativa, permitir 5 dispositivos
    if (profile?.subscription_status === 'active') {
      return {
        limit: 5, // Limite para usuários com plano ativo
        planName: 'Plano Ativo'
      };
    }

    // Sem plano ativo - permitir 1 dispositivo
    return {
      limit: 1, // Permitir 1 dispositivo mesmo sem plano
      planName: 'Plano Básico'
    };

  } catch (error) {
    console.error('Erro ao verificar limite do plano:', error);
    return {
      limit: 1, // Permitir 1 dispositivo em caso de erro
      planName: 'Plano Básico'
    };
  }
};

// Função para verificar limite de dispositivos
const checkDeviceLimit = async (userId: string): Promise<{ canActivate: boolean; currentCount: number; limit: number; planName: string }> => {
  try {
    // Contar dispositivos ativos do usuário
    const { count: deviceCount, error: deviceError } = await supabase
      .from('devices')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .eq('status', 'active');

    if (deviceError) {
      console.error('Erro ao contar dispositivos:', deviceError);
      throw new Error('Erro ao contar dispositivos');
    }

    const currentCount = deviceCount || 0;

    // Obter limite do plano do usuário
    const { limit, planName } = await getUserPlanDeviceLimit(userId);

    return {
      canActivate: limit > 0 && currentCount < limit,
      currentCount,
      limit,
      planName
    };

  } catch (error) {
    console.error('Erro ao verificar limite de dispositivos:', error);
    return {
      canActivate: false,
      currentCount: 0,
      limit: 0,
      planName: 'Erro ao verificar plano'
    };
  }
};

export const useDevices = (userId?: string, overrideOrganizationId?: string) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchDevices = async () => {
    console.log('🔍 useDevices - Iniciando fetchDevices para userId:', userId);
    if (!userId) {
      console.log('🔍 useDevices - Sem userId, retornando');
      setLoading(false);
      return;
    }

    try {
      console.log('🔍 useDevices - Buscando organização do usuário via mapeamento');
      let organizationId = overrideOrganizationId || null;
      if (!organizationId) {
        const { data: orgMap, error: orgError } = await supabase
          .from('organizations_users')
          .select('organization_id')
          .eq('user_id', userId)
          .limit(1);
        console.log('🔍 useDevices - Resultado da organização:', { orgMap, orgError });
        if (orgError || !orgMap || !orgMap[0]?.organization_id) {
          console.warn('🔍 useDevices - Organização não encontrada, aplicando fallback user_id');
          const { data: byUserRpc, error: byUserErr } = await supabase
            .rpc('admin_get_user_devices', { p_user_id: userId });
          if (!byUserErr) {
            setDevices((byUserRpc as any[]) || []);
            setLoading(false);
            return;
          }
          setLoading(false);
          return;
        }
        organizationId = orgMap[0].organization_id as string;
      }
      console.log('🔍 useDevices - Buscando dispositivos para organização:', organizationId);

      // Buscar dispositivos da organização
      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      console.log('🔍 useDevices - Resultado da busca de dispositivos:', { data, error });

      if (error) {
        console.error('🔍 useDevices - Erro SQL:', error);
        throw error;
      }

      console.log('🔍 useDevices - Dispositivos encontrados:', data?.length || 0);
      setDevices(data || []);
    } catch (error) {
      console.error('🔍 useDevices - Erro ao carregar dispositivos:', error);
      toast({
        title: "Erro ao carregar dispositivos",
        description: "Não foi possível carregar os dispositivos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const activateDevice = async (code: string, screenId: string, deviceName?: string) => {
    try {
      if (!userId) {
        throw new Error('Usuário não autenticado');
      }

      // Verificar limite de dispositivos antes de ativar
      const limitCheck = await checkDeviceLimit(userId);
      if (!limitCheck.canActivate) {
        if (limitCheck.limit === 0) {
          throw new Error(`Você não possui um plano ativo. Por favor, adquira um plano para ativar dispositivos.`);
        } else {
          throw new Error(`Limite de dispositivos atingido. Seu plano ${limitCheck.planName} permite apenas ${limitCheck.limit} ${limitCheck.limit === 1 ? 'dispositivo' : 'dispositivos'}. Você já tem ${limitCheck.currentCount} ${limitCheck.currentCount === 1 ? 'dispositivo ativo' : 'dispositivos ativos'}.`);
        }
      }

      // Buscar organização do usuário via mapeamento organizations_users
      let organization_id = overrideOrganizationId || null;
      if (!organization_id) {
        const { data: orgMap, error: orgError } = await supabase
          .from('organizations_users')
          .select('organization_id')
          .eq('user_id', userId)
          .limit(1);

        if (orgError) {
          console.error('Erro ao buscar organização:', orgError);
          throw new Error('Não foi possível verificar sua organização. Contate o suporte.');
        }
        if (!orgMap || !orgMap[0]?.organization_id) {
          console.error('Organização não encontrada para o usuário');
          throw new Error('Sua conta não está associada a uma organização. Contate o suporte.');
        }
        organization_id = orgMap[0].organization_id as string;
      }

      // Padronizar código
      const formattedCode = code.toUpperCase();

      // Verificar se o usuário tem permissão na organização
      const { data: membership, error: membershipError } = await supabase
        .from('organizations_users')
        .select('*')
        .eq('organization_id', organization_id)
        .eq('user_id', userId)
        .single();

      if (membershipError || !membership) {
        console.error('Erro de permissão na organização:', membershipError);
        throw new Error('Você não tem permissão para gerenciar dispositivos nesta organização');
      }

      // Verificar se já existe device com esse código
      const { data: existingDevice, error: checkError } = await supabase
        .from('devices')
        .select('id, user_id, organization_id, status')
        .eq('code', formattedCode)
        .maybeSingle();

      if (checkError) {
        console.error('Erro ao verificar dispositivo existente:', checkError);
        throw new Error('Erro ao verificar o código do dispositivo');
      }

      let data, error;

      try {
        if (existingDevice) {
          // Verificar se o dispositivo pertence à mesma organização
          if (existingDevice.organization_id !== organization_id) {
            throw new Error('Este código já está sendo usado por outro usuário');
          }

          // Se existe na mesma organização, faça update
          ({ data, error } = await supabase
            .from('devices')
            .update({
              user_id: userId,
              organization_id,
              name: deviceName || `Dispositivo ${formattedCode}`,
              screen_id: screenId,
              status: 'active',
              activated_at: new Date().toISOString(),
              is_active: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingDevice.id)
            .select()
            .single());
        } else {
          // Se não existe, faça insert
          ({ data, error } = await supabase
            .from('devices')
            .insert({
              user_id: userId,
              organization_id,
              code: formattedCode,
              name: deviceName || `Dispositivo ${formattedCode}`,
              screen_id: screenId,
              status: 'active',
              activated_at: new Date().toISOString(),
              is_active: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select()
            .single());
        }
      } catch (insertError: any) {
        console.error('Erro na operação com o banco de dados:', insertError);

        // Verificar se é erro de política RLS
        if (insertError.code === '42501') {
          throw new Error('Erro de permissão: Você não tem acesso para realizar esta operação. Verifique se seu plano está ativo.');
        }

        throw new Error('Erro ao salvar dispositivo: ' + (insertError.message || 'Erro desconhecido'));
      }

      if (error) {
        console.error('Erro SQL:', error);

        // Verificar se é erro de política RLS
        if (error.code === '42501') {
          throw new Error('Erro de permissão: Você não tem acesso para realizar esta operação. Verifique se seu plano está ativo.');
        }

        throw error;
      }

      setDevices(prev => [data, ...prev.filter(d => d.code !== formattedCode)]);

      // Log de sucesso para fins de depuração
      console.log('Dispositivo ativado com sucesso:', data);

      toast({
        title: "Dispositivo ativado com sucesso!",
        description: `Dispositivo "${data.name}" foi ativado`,
      });

      // Retorna os dados do dispositivo para que o componente possa usá-los
      return data;
    } catch (error) {
      console.error('Erro ao ativar dispositivo:', error);

      let errorMessage = "Não foi possível ativar o dispositivo. Verifique as permissões e tente novamente.";

      if (error instanceof Error) {
        if (error.message.includes('Limite de dispositivos atingido') ||
          error.message.includes('não possui um plano ativo') ||
          error.message.includes('Erro de permissão') ||
          error.message.includes('Você não tem permissão') ||
          error.message.includes('já está sendo usado') ||
          error.message.includes('não está associada') ||
          error.message.includes('Erro ao salvar dispositivo')) {
          errorMessage = error.message;
        }
      }

      toast({
        title: "Erro ao ativar dispositivo",
        description: errorMessage,
        variant: "destructive",
      });
      return null;
    }
  };

  const updateDevice = async (id: string, updates: Omit<DeviceUpdate, 'user_id'>) => {
    try {
      // Detectar modo impersonate
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const impersonateUserId = urlParams?.get('impersonate');
      const isImpersonating = !!impersonateUserId;

      // Buscar dispositivo atual primeiro
      const { data: currentDevice } = await supabase
        .from('devices')
        .select('*')
        .eq('id', id)
        .single();

      if (!currentDevice) {
        throw new Error('Dispositivo não encontrado');
      }

      let data: any = null;

      if (isImpersonating) {
        // Modo Admin: usar RPC para atualizar dispositivo
        console.log('👑 useDevices - Modo Admin Impersonate: Atualizando dispositivo via RPC admin');
        const result = await supabase.rpc('admin_update_device', {
          p_device_id: id,
          p_name: updates.name || null,
          p_screen_id: updates.screen_id || null,
          p_active: null,
          p_settings: updates.settings ? JSON.stringify(updates.settings) : null
        });

        if (result.error) throw result.error;

        const response = result.data as { success: boolean; data?: any; error?: string };
        if (!response.success) {
          throw new Error(response.error || 'Erro ao atualizar dispositivo via admin');
        }
        data = response.data;
      } else {
        // Modo normal: atualizar diretamente
        const result = await supabase
          .from('devices')
          .update(updates)
          .eq('id', id)
          .eq('user_id', userId)
          .select()
          .single();

        if (result.error) throw result.error;
        data = result.data;
      }

      // Atualizar estado local
      setDevices(prev => prev.map(device =>
        device.id === id ? { ...device, ...data } : device
      ));

      toast({
        title: "Dispositivo atualizado",
        description: updates.name ? `Nome alterado para "${updates.name}"` : "Atualização realizada com sucesso",
      });

      return data;
    } catch (error) {
      console.error('Erro ao atualizar dispositivo:', error);
      toast({
        title: "Erro ao atualizar dispositivo",
        description: "Não foi possível atualizar o dispositivo",
        variant: "destructive",
      });
      return null;
    }
  };

  const removeDevice = async (id: string) => {
    try {
      console.log('🗑️ useDevices - Removendo dispositivo:', { id, userId });

      // Buscar o dispositivo primeiro para debug
      const { data: deviceToDelete, error: fetchError } = await supabase
        .from('devices')
        .select('*')
        .eq('id', id);

      if (fetchError) {
        console.error('🗑️ useDevices - Erro ao buscar dispositivo:', fetchError);
      } else {
        console.log('🗑️ useDevices - Dispositivo encontrado:', deviceToDelete);
      }

      // Tentar delete primeiro só com ID (mais permissivo)
      const { error: deleteError } = await supabase
        .from('devices')
        .delete()
        .eq('id', id);

      if (deleteError) {
        console.error('🗑️ useDevices - Erro no delete:', deleteError);
        throw deleteError;
      }

      console.log('🗑️ useDevices - Dispositivo removido com sucesso do banco');

      // Verificar se realmente foi removido
      const { data: checkDeleted, error: checkError } = await supabase
        .from('devices')
        .select('*')
        .eq('id', id);

      if (checkError) {
        console.error('🗑️ useDevices - Erro ao verificar remoção:', checkError);
      } else {
        console.log('🗑️ useDevices - Verificação pós-remoção:', {
          found: checkDeleted?.length || 0,
          data: checkDeleted
        });
      }

      // Atualizar estado local
      setDevices(prev => {
        const updated = prev.filter(device => device.id !== id);
        console.log('🗑️ useDevices - Estado atualizado:', { before: prev.length, after: updated.length });
        return updated;
      });

      toast({
        title: "Dispositivo removido",
        description: "O dispositivo foi removido com sucesso",
      });

      return true;
    } catch (error) {
      console.error('🗑️ useDevices - Erro ao remover dispositivo:', error);
      toast({
        title: "Erro ao remover dispositivo",
        description: "Não foi possível remover o dispositivo",
        variant: "destructive",
      });
      return false;
    }
  };

  // Atualizar tela associada do dispositivo no Supabase
  const updateDeviceScreen = async (deviceId: string, screenId: string) => {
    try {
      const { data, error } = await supabase
        .from('devices')
        .update({
          screen_id: screenId,
          status: 'active',
          activated_at: new Date().toISOString()
        })
        .eq('id', deviceId)
        .select()
        .single();

      if (error) throw error;

      setDevices(prev => prev.map(device =>
        device.id === deviceId ? { ...device, screen_id: screenId, status: 'active', activated_at: new Date().toISOString() } : device
      ));

      toast({
        title: "Tela associada atualizada",
        description: `O dispositivo foi associado à nova tela com sucesso.`
      });

      return data;
    } catch (error) {
      console.error('Erro ao atualizar tela associada:', error);
      toast({
        title: "Erro ao atualizar tela",
        description: "Não foi possível atualizar a tela associada ao dispositivo.",
        variant: "destructive",
      });
      return null;
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [userId]);

  return {
    devices,
    loading,
    activateDevice,
    updateDevice,
    removeDevice,
    refetch: fetchDevices,
    updateDeviceScreen,
  };
};

// Função para buscar aparelhos filtrando pelo nome ou código
export const fetchDevicesByQuery = async (userId: string, query: string): Promise<Device[]> => {
  if (!userId) return [];
  try {
    // Buscar organização do usuário via função RPC
    const { data: orgData, error: orgError } = await supabase
      .rpc('get_user_organization');
    if (orgError || !orgData || !orgData[0]?.organization_id) return [];
    let supabaseQuery = supabase
      .from('devices')
      .select('*')
      .eq('organization_id', orgData[0].organization_id)
      .order('created_at', { ascending: false });
    if (query && query.trim() !== "") {
      supabaseQuery = supabaseQuery.or(`name.ilike.%${query}%,code.ilike.%${query}%`);
    }
    const { data, error } = await supabaseQuery;
    if (error || !data) return [];
    return data as Device[];
  } catch {
    return [];
  }
};
